import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests
api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors
api.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authApi = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me')
};

// Employees
export const employeesApi = {
  getAll: (params) => api.get('/employees', { params }),
  getById: (id) => api.get(`/employees/${id}`),
  create: (data) => api.post('/employees', data),
  update: (id, data) => api.put(`/employees/${id}`, data),
  delete: (id) => api.delete(`/employees/${id}`)
};

// Attendance
export const attendanceApi = {
  clockIn: () => api.post('/attendance/clock-in'),
  clockOut: () => api.post('/attendance/clock-out'),
  getAll: (params) => api.get('/attendance', { params }),
  getToday: () => api.get('/attendance/today'),
  getDaySummary: (month, year) => api.get('/attendance/day-summary', { params: { month, year } }),
  getLeaveHistory: (params) => api.get('/attendance/leave-history', { params })
};

// Leaves
export const leavesApi = {
  apply: (data) => api.post('/leaves', null, { params: data }),
  getAll: (params) => api.get('/leaves', { params }),
  approve: (id) => api.put(`/leaves/${id}/approve`),
  reject: (id) => api.put(`/leaves/${id}/reject`),
  getBalance: (employeeId) => api.get(`/leaves/balance/${employeeId}`)
};

// Payroll
export const payrollApi = {
  generate: (month, year) => api.post('/payroll/generate', null, { params: { month, year } }),
  regenerate: (month, year) => api.post('/payroll/regenerate', null, { params: { month, year } }),
  importFromExcel: () => api.post('/payroll/import-from-excel'),
  getAll: (params) => api.get('/payroll', { params }),
  getById: (id) => api.get(`/payroll/${id}`),
  update: (id, data) => api.put(`/payroll/${id}`, null, { params: data })
};

// Compliance
export const complianceApi = {
  getAll: (params) => api.get('/compliance', { params }),
  getSummary: (month, year) => api.get('/compliance/summary', { params: { month, year } })
};

// Holidays
export const holidaysApi = {
  getAll: (year) => api.get('/holidays', { params: { year } }),
  create: (data) => api.post('/holidays', null, { params: data })
};

// Dashboard
export const dashboardApi = {
  getStats: () => api.get('/dashboard/stats'),
  getAttendanceTrend: (days = 7) => api.get('/dashboard/attendance-trend', { params: { days } }),
  getPayrollTrend: (months = 6) => api.get('/dashboard/payroll-trend', { params: { months } })
};

// Departments
export const departmentsApi = {
  getAll: () => api.get('/departments')
};

// Recruitment & Hiring
export const recruitmentApi = {
  getJobs: (params) => api.get('/jobs', { params }),
  createJob: (data) => api.post('/jobs', data),
  updateJob: (id, data) => api.put(`/jobs/${id}`, data),
  deleteJob: (id) => api.delete(`/jobs/${id}`),
  getApplications: (params) => api.get('/applications', { params }),
  createApplication: (data) => api.post('/applications', data),
  updateApplication: (id, data) => api.put(`/applications/${id}`, data)
};

// Performance Management
export const performanceApi = {
  getReviews: (params) => api.get('/performance/reviews', { params }),
  createReview: (data) => api.post('/performance/reviews', data),
  updateReview: (id, data) => api.put(`/performance/reviews/${id}`, data),
  getGoals: (params) => api.get('/goals', { params }),
  createGoal: (data) => api.post('/goals', data),
  updateGoal: (id, data) => api.put(`/goals/${id}`, data)
};

// Training & Development
export const trainingApi = {
  getPrograms: (params) => api.get('/training/programs', { params }),
  createProgram: (data) => api.post('/training/programs', data),
  updateProgram: (id, data) => api.put(`/training/programs/${id}`, data),
  getEnrollments: (params) => api.get('/training/enrollments', { params }),
  enroll: (data) => api.post('/training/enroll', data),
  updateEnrollment: (id, data) => api.put(`/training/enrollments/${id}`, data),
  getCertifications: (params) => api.get('/certifications', { params }),
  addCertification: (data) => api.post('/certifications', data),
  updateCertification: (id, data) => api.put(`/certifications/${id}`, data)
};

// Document Management
export const documentsApi = {
  getAll: (params) => api.get('/documents', { params }),
  upload: (data) => api.post('/documents', data),
  update: (id, data) => api.put(`/documents/${id}`, data),
  delete: (id) => api.delete(`/documents/${id}`)
};

// Export Functions
export const exportApi = {
  payroll: (month, year, format = 'excel') => 
    api.get(`/export/payroll`, { params: { month, year, format }, responseType: 'blob' }),
  attendance: (month, year, format = 'excel') => 
    api.get(`/export/attendance`, { params: { month, year, format }, responseType: 'blob' }),
  employees: (format = 'excel') => 
    api.get(`/export/employees`, { params: { format }, responseType: 'blob' })
};

// Seed data
export const seedApi = {
  seed: () => api.post('/seed'),
  reset: () => api.post('/seed?force=true')
};

export default api;
